import { DataTablePFSEvent } from "primereact/datatable";
import React from "react";
import config from "../../../config";
import { GeneratedReport } from "../interfaces/GeneratedReport";
import { EMPTY_PAGED_GENERATED_REPORTS } from "../interfaces/GeneratedReportsAPIInterfaces";
import { ReportDocument } from "../interfaces/ReportDocument";
import { ReportingMeasurement } from "../interfaces/ReportingMeasurement";
import { ReportingPayload } from "../interfaces/ReportingPayload";
import { EMPTY_PAGED_REPORTS, PagedData } from "../interfaces/ReportsAPIInterfaces";
import { ReportStatusUpdate, ResultAssetAndCaptureIds } from "../interfaces/Result";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";

export type ReportsListRequestType =
  | { type: "byCustomer"; customerId: number }
  | { type: "byCompany"; companyId: number }
  | { type: "bySite"; siteId: number };

interface Props {
  onAuthError?: () => void;
}

export default class UTP2ReportsAPIs extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });

  private static readonly BASE_URL: string = `${config.apiGateway.UTP2_API}/api/Reports/`;

  async uploadReportMeasurementSelections(
    reportingMeasurementSelections: ReportingMeasurement,
  ): Promise<ReportingPayload> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}Measurement`;

    const payloadString = JSON.stringify(reportingMeasurementSelections);

    return await this.apiResponseHandler.post(apiAddress, payloadString);
  }

  async removeReportMeasurementSelections(reportingMeasurement?: ReportingMeasurement): Promise<ReportingPayload> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}Measurement`;

    const payloadString = JSON.stringify(reportingMeasurement);

    return await this.apiResponseHandler.delete(apiAddress, payloadString);
  }

  async getReportMeasurementSelections(
    customerId: number,
    resultAssetAndCaptureIdList: ResultAssetAndCaptureIds[] | undefined,
  ): Promise<ReportingMeasurement[]> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}Measurement/Customer/${customerId}`;

    return await this.apiResponseHandler.post(apiAddress, JSON.stringify(resultAssetAndCaptureIdList), false);
  }

  async getReportMeasurementSelectionsByResultCapture(
    captureId: number,
    customerId: number,
    assetId: number,
  ): Promise<ReportingMeasurement[]> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}Measurement/Capture/${captureId}/Customer/${customerId}/Asset/${assetId}`;

    return await this.apiResponseHandler
      .get(apiAddress)
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.error("Error getting report measurement selections by capture", error);
        throw error;
      });
  }

  getPagedReportsList = async (
    lazyState: DataTablePFSEvent,
    customerId: number,
    companyId?: number,
    siteId?: number,
  ): Promise<PagedData<ReportDocument[]>> => {
    if (companyId && siteId) {
      return await this.getPagedReports(
        `${UTP2ReportsAPIs.BASE_URL}ByCustomerCompanyAndSite/customerId/${customerId}/companyId/${companyId}/siteId/${siteId}`,
        lazyState,
      );
    } else if (companyId) {
      return await this.getPagedReports(
        `${UTP2ReportsAPIs.BASE_URL}ByCustomerAndCompany/customerId/${customerId}/companyId/${companyId}`,
        lazyState,
      );
    } else {
      return await this.getPagedReports(`${UTP2ReportsAPIs.BASE_URL}ByCustomer/${customerId}`, lazyState);
    }
  };

  private getPagedReports = async (
    apiAddress: string,
    lazyState: DataTablePFSEvent,
  ): Promise<PagedData<ReportDocument[]>> => {
    const response = await this.apiResponseHandler.post(apiAddress, JSON.stringify(lazyState), false);

    if (!response) return Promise.resolve(EMPTY_PAGED_REPORTS);

    return response;
  };

  deleteReport = (reportId: number): Promise<void> => {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}${reportId}`;
    return this.apiResponseHandler.delete(apiAddress);
  };

  async getReportDocumentById(reportDocumentId: number): Promise<ReportDocument> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}ReportDocument/${reportDocumentId}`;
    return await this.apiResponseHandler.get(apiAddress);
  }

  async getPagedGeneratedReportsByReportDocumentId(
    lazyState: DataTablePFSEvent,
    reportDocumentId: number,
    customerId: number,
    companyId: number,
    siteId: number,
  ): Promise<PagedData<GeneratedReport[]>> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}GeneratedReports/ReportDocument/${reportDocumentId}/Customer/${customerId}/Company/${companyId}/Site/${siteId}`;

    const response = await this.apiResponseHandler.post(apiAddress, JSON.stringify(lazyState), false);

    if (!response) return Promise.resolve(EMPTY_PAGED_GENERATED_REPORTS);

    return response;
  }

  async deleteGeneratedReport(generatedReportId: number): Promise<null> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}GeneratedReport/${generatedReportId}`;
    return this.apiResponseHandler.delete(apiAddress, false);
  }

  async updateReportStatus(reportStatusUpdate: ReportStatusUpdate): Promise<void> {
    const apiAddress = `${UTP2ReportsAPIs.BASE_URL}Status`;

    return await this.apiResponseHandler.put(apiAddress, JSON.stringify(reportStatusUpdate), false);
  }
}
